import debounce from 'lodash.debounce'
import {
  createContext,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import { calculateVerticalDistanceFromWindow } from '@f-fertighaus/utils/scrollingUtils'

export enum HdpNavigationTab {
  overview,
  floorPlans,
  details,
  price,
  vendor
}
const INITIAL_ACTIVE_TAB_INDEX = 0
const HDP_NAVIGATION_TABS: HdpNavigationTab[] = [
  HdpNavigationTab.overview,
  HdpNavigationTab.floorPlans,
  HdpNavigationTab.details,
  HdpNavigationTab.price,
  HdpNavigationTab.vendor
]

interface HouseDetailTabsContext {
  activeTabIndex: number
  shouldShowOnDesktop: boolean
  handleTabChange: (tabIndex: number) => void
  handleTabClick: (event: SyntheticEvent, tabIndex: number) => void
}

const HouseDetailTabsContext = createContext<
  HouseDetailTabsContext | undefined
>(undefined)

export function useActiveTabs() {
  const context = useContext(HouseDetailTabsContext)

  if (!context) {
    throw new Error('useActiveTabs must be used within HouseDetailTabsProvider')
  }

  return context
}

export const HouseDetailTabsProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const [activeTab, setActiveTab] = useState(INITIAL_ACTIVE_TAB_INDEX)
  const [shouldShowOnDesktop, setShouldShowOnDesktop] = useState(false)

  useEffect(() => {
    const changeActiveTab = () => {
      const currentTabProps = HDP_NAVIGATION_TABS.reduce(
        (acc: { index: number; positionY: number } | null, _, index) => {
          const element = document.getElementById(
            `navigation-category-${index}`
          )
          if (element) {
            const positionY = calculateVerticalDistanceFromWindow(element)
            return !acc || (positionY < 0 && positionY > acc.positionY)
              ? { index, positionY }
              : acc
          }
          return acc
        },
        null
      )
      if (currentTabProps) {
        setActiveTab(currentTabProps.index)
        setShouldShowOnDesktop(currentTabProps.positionY < 0)
      }
    }

    const changeActiveTabDebounced = debounce(changeActiveTab, 100)
    document.addEventListener('scroll', changeActiveTabDebounced)

    return () =>
      document.removeEventListener('scroll', changeActiveTabDebounced)
  }, [])

  const handleTabChange = useCallback(function (
    newValue: HdpNavigationTab
  ): void {
    const node = document.getElementById(`navigation-category-${newValue}`)
    if (newValue === 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    } else if (node) {
      const swiperHeight = document.querySelector('.swiper')?.clientHeight || 0 // swiper is not accounted in the window height

      window.scrollTo({
        top: node.offsetTop + swiperHeight,
        behavior: 'smooth'
      })
    }
  },
  [])

  const handleTabClick = (event: SyntheticEvent, newValue: number) => {
    handleTabChange(newValue)
  }

  const value = {
    activeTabIndex: activeTab,
    shouldShowOnDesktop,
    handleTabClick,
    handleTabChange
  }

  return (
    <HouseDetailTabsContext.Provider value={value}>
      {children}
    </HouseDetailTabsContext.Provider>
  )
}
